import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthInternal } from '../auth/AuthProvider';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Handles the Entra ID OAuth2 callback.
 * Extracts the authorization code from URL params and exchanges it
 * via the backend for a session cookie.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { markAuthenticated } = useAuthInternal();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      if (!code) {
        setError('No authorization code received from Entra ID.');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/entra/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });

        if (response.ok) {
          const data = await response.json();
          markAuthenticated(data.user || data);
          navigate('/', { replace: true });
        } else {
          const errorData = await response.json().catch(() => null);
          setError(
            errorData?.error?.message || errorData?.message || 'Authentication failed. Please try again.',
          );
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Network error during authentication.',
        );
      }
    }

    handleCallback();
  }, [searchParams, navigate, markAuthenticated]);

  if (error) {
    return (
      <div className="auth-error" role="alert">
        <h2>Authentication Failed</h2>
        <p>{error}</p>
        <a href="/" className="btn btn-primary">
          Return to Login
        </a>
      </div>
    );
  }

  return (
    <div className="auth-loading" role="status" aria-label="Processing authentication">
      <div className="auth-loading__spinner" />
      <p>Completing authentication...</p>
    </div>
  );
}
